import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import Select from 'react-select';
import { FaUser, FaEnvelope, FaPhone, FaChartLine, FaCreditCard, FaHome, FaMoneyBillWave, FaPlus } from 'react-icons/fa';

// Case type mapping (can be moved to a constants file later if needed)
const caseTypeMap = {
  'Credit Card': { id: 1, icon: <FaCreditCard className="me-2" /> },
  'Personal Loan': { id: 2, icon: <FaMoneyBillWave className="me-2" /> },
  'Home Loan': { id: 3, icon: <FaHome className="me-2" /> },
  'Business Loan': { id: 4, icon: <FaMoneyBillWave className="me-2" /> },
  'LAP': { id: 5, icon: <FaHome className="me-2" /> }
};

function AddLeadForm({
  isOpen,
  onClose,
  onSubmit, // Expects an async function: (leadData, agentId) => Promise<void>
  agents = [],
  loadingAgents = false,
  products = [],
  loadingProducts = false,
  fetchProducts, // Expects: (categoryId) => Promise<void>
  initialAgentId = '', // Optional: Pre-select agent
}) {
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    mobile: '',
    case_type: 'Credit Card', // Default to Credit Card
    product_id: '',
    agent_id: initialAgentId || '', // Use initialAgentId if provided
    loan_amount: '',
    person_type: 'Individual', // Default to Individual
    property_identified: 'No', // Default to No
    property_value: '',
    property_city: '',
    total_monthly_emi: '',
    business_type: ''
  });
  const [validated, setValidated] = useState(false);
  const [addLeadError, setAddLeadError] = useState('');
  const [addLeadLoading, setAddLeadLoading] = useState(false);

  // Reset form when modal opens or initialAgentId changes
  useEffect(() => {
    if (isOpen) {
      setNewLead({
        name: '',
        email: '',
        mobile: '',
        case_type: 'Credit Card',
        product_id: '',
        agent_id: initialAgentId || '',
        loan_amount: '',
        person_type: 'Individual',
        property_identified: 'No',
        property_value: '',
        property_city: '',
        total_monthly_emi: '',
        business_type: ''
      });
      setValidated(false);
      setAddLeadError('');
      // Fetch initial products for default category (Credit Card)
      if (fetchProducts) {
          fetchProducts(caseTypeMap['Credit Card'].id);
      }
    }
  }, [isOpen, initialAgentId, fetchProducts]);


  const handleNewLeadChange = (e) => {
    const { name, value } = e.target;
    setAddLeadError(''); // Clear error on change

    if (name === 'mobile') {
      if (/^\d*$/.test(value) && value.length <= 10) {
        setNewLead(prev => ({ ...prev, [name]: value }));
      }
    } else if (name === 'loan_amount') {
      if (/^\d*\.?\d*$/.test(value)) {
        setNewLead(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setNewLead(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePropertyIdentifiedChange = (value) => {
    setAddLeadError(''); // Clear error on change
    setNewLead(prev => ({
      ...prev,
      property_identified: value,
      // Reset property fields if "No" is selected
      ...(value === 'No' && {
        property_value: '',
        property_city: '',
        total_monthly_emi: ''
      })
    }));
  };

  const handleSelectChange = (field, selectedOption) => {
    setAddLeadError(''); // Clear error on change

    if (field === 'agent_id') {
      setNewLead(prev => ({
        ...prev,
        agent_id: selectedOption ? selectedOption.value : ''
      }));
    } else if (field === 'case_type') {
        const newCaseType = selectedOption ? selectedOption.value : '';
        setNewLead(prev => ({
            ...prev,
            case_type: newCaseType,
            product_id: '' // Reset product on category change
        }));
        if (selectedOption && fetchProducts) {
            const categoryId = caseTypeMap[newCaseType]?.id;
            if (categoryId) {
                fetchProducts(categoryId);
            } else {
                fetchProducts(); // Fetch all if no specific category ID
            }
        } else if (fetchProducts) {
            fetchProducts(); // Fetch all if category cleared
        }
    } else if (field === 'product_id') {
        setNewLead(prev => ({
            ...prev,
            product_id: selectedOption ? selectedOption.value : ''
        }));
    } else if (field === 'person_type') {
        setNewLead(prev => ({
            ...prev,
            person_type: selectedOption ? selectedOption.value : ''
        }));
    }
  };

  const handleInternalSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const isLoanCategory = ['Home Loan', 'Personal Loan', 'Business Loan', 'LAP'].includes(newLead.case_type);

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    } 

    setAddLeadError('');

    // Agent validation - only required if not pre-selected
    if (!initialAgentId && !newLead.agent_id) {
      setAddLeadError('Please select an agent to assign this lead to');
      return;
    }

    setAddLeadLoading(true);

    try {
      const leadData = {
        name: newLead.name,
        email: newLead.email,
        mobile: newLead.mobile,
        case_type: newLead.case_type,
        person_type: newLead.person_type,
        property_identified: ['Home Loan', 'LAP'].includes(newLead.case_type) ? newLead.property_identified : undefined,
        business_type: newLead.person_type === 'Business' ? newLead.business_type : undefined,
      };
      
      // Add property details if identified as Yes
      if (['Home Loan', 'LAP'].includes(newLead.case_type) && newLead.property_identified === 'Yes') {
        if (newLead.property_value) {
          const propertyValue = parseFloat(newLead.property_value);
          if (!isNaN(propertyValue) && propertyValue > 0) {
            leadData.property_value = propertyValue;
          }
        }
        
        if (newLead.property_city) {
          leadData.property_city = newLead.property_city;
        }
        
        if (newLead.total_monthly_emi) {
          const monthlyEmi = parseFloat(newLead.total_monthly_emi);
          if (!isNaN(monthlyEmi) && monthlyEmi > 0) {
            leadData.total_monthly_emi = monthlyEmi;
          }
        }
      }

      if (newLead.product_id) {
        leadData.product_id = newLead.product_id;
      }

      if (isLoanCategory && newLead.loan_amount) {
        const loanAmountValue = parseFloat(newLead.loan_amount);
        if (!isNaN(loanAmountValue) && loanAmountValue > 0) {
          leadData.loan_amount = loanAmountValue;
        } else if (loanAmountValue <= 0) {
          setAddLeadError('Loan amount must be a positive number if provided.');
          setAddLeadLoading(false);
          setValidated(true);
          return;
        }
      }

      // Call the onSubmit prop passed from the parent
      await onSubmit(leadData, newLead.agent_id);

      // Parent component is responsible for closing modal and showing success message
      // onClose(); // Let parent handle closing

    } catch (err) {
      console.error('Error submitting lead:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create lead. Please try again.';
      setAddLeadError(errorMessage);
    } finally {
      setAddLeadLoading(false);
    }
  };

  const currentCaseTypeOption = newLead.case_type ?
    {
        value: newLead.case_type,
        label: newLead.case_type,
        icon: caseTypeMap[newLead.case_type]?.icon
    } : null;

  const currentProductOption = newLead.product_id ?
    products.map(p => ({
        value: p.id.toString(),
        label: `${p.name} - ${p.bank?.bank || 'N/A'}`
    })).find(opt => opt.value === newLead.product_id.toString())
    : null;

  const currentAgentOption = newLead.agent_id ?
    agents.map(a => ({
        value: a.id.toString(),
        label: a.name
    })).find(opt => opt.value === newLead.agent_id.toString())
    : null;


  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add New Lead</Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleInternalSubmit}>
        <Modal.Body>
          {addLeadError && (
            <Alert variant="danger" className="mb-3">
              {addLeadError}
            </Alert>
          )}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaUser /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newLead.name}
                    onChange={handleNewLeadChange}
                    required
                    minLength={3}
                    placeholder="Enter full name"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a name (minimum 3 characters).
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    value={newLead.email}
                    onChange={handleNewLeadChange}
                    required
                    placeholder="example@email.com"
                    pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid email address.
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaPhone /></InputGroup.Text>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={newLead.mobile}
                    onChange={handleNewLeadChange}
                    pattern="[0-9]{10}"
                    required
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid 10-digit mobile number.
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Category"
                  options={Object.entries(caseTypeMap).map(([label, { icon }]) => ({
                    value: label,
                    label: label,
                    icon: icon
                  }))}
                  value={currentCaseTypeOption}
                  onChange={(option) => handleSelectChange('case_type', option)}
                  formatOptionLabel={option => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {option.icon}
                      <span>{option.label}</span>
                    </div>
                  )}
                  required
                />
                <Form.Control
                  hidden
                  required
                  value={newLead.case_type}
                  isInvalid={validated && !newLead.case_type}
                />
                <Form.Control.Feedback type="invalid">
                  Please select a category.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Person Type</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Person Type"
                  options={[
                    { value: "Individual", label: "Individual" },
                    { value: "Business", label: "Business" }
                  ]}
                  value={{
                    value: newLead.person_type || "Individual",
                    label: newLead.person_type || "Individual"
                  }}
                  onChange={(option) => handleSelectChange('person_type', option)}
                  required
                />
                <Form.Control
                  hidden
                  required
                  value={newLead.person_type}
                  isInvalid={validated && !newLead.person_type}
                />
                <Form.Control.Feedback type="invalid">
                  Please select a person type.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            {newLead.person_type === 'Business' && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Business Type</Form.Label>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Select Business Type"
                    options={[
                      { value: "Sole Proprietorship", label: "Sole Proprietorship" },
                      { value: "Partnership", label: "Partnership" },
                      { value: "LLC", label: "LLC" },
                      { value: "Private Ltd", label: "Private Ltd" },
                      { value: "Public Ltd", label: "Public Ltd" }
                    ]}
                    value={newLead.business_type ? { value: newLead.business_type, label: newLead.business_type } : null}
                    onChange={(selectedOption) => {
                      setNewLead(prev => ({
                        ...prev,
                        business_type: selectedOption ? selectedOption.value : ''
                      }));
                    }}
                    required={newLead.person_type === 'Business'}
                  />
                  {newLead.person_type === 'Business' && (
                    <Form.Control
                      hidden
                      required
                      value={newLead.business_type}
                      isInvalid={validated && !newLead.business_type && newLead.person_type === 'Business'}
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    Please select a business type.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </Row>

          <Row>
            {['Home Loan', 'Personal Loan', 'Business Loan', 'LAP'].includes(newLead.case_type) && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Loan Amount</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        name="loan_amount"
                        value={newLead.loan_amount}
                        onChange={handleNewLeadChange}
                        placeholder="Enter desired loan amount (Optional)"
                        min="0"
                        step="any"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid loan amount if entering one.
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
            )}
            
            {['Home Loan', 'LAP'].includes(newLead.case_type) && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Property Identified</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      id="property-yes"
                      name="property_identified"
                      label="Yes"
                      checked={newLead.property_identified === 'Yes'}
                      onChange={() => handlePropertyIdentifiedChange('Yes')}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="property-no"
                      name="property_identified"
                      label="No"
                      checked={newLead.property_identified === 'No'}
                      onChange={() => handlePropertyIdentifiedChange('No')}
                    />
                  </div>
                </Form.Group>
              </Col>
            )}
            
            {['Home Loan', 'LAP'].includes(newLead.case_type) && newLead.property_identified === 'Yes' && (
              <>
                <Row className="mt-2">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Property Value</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          name="property_value"
                          value={newLead.property_value}
                          onChange={handleNewLeadChange}
                          placeholder="Enter property value"
                          min="0"
                          step="any"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid property value.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Property City</Form.Label>
                      <Form.Control
                        type="text"
                        name="property_city"
                        value={newLead.property_city}
                        onChange={handleNewLeadChange}
                        placeholder="Enter property city"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a property city.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Monthly EMI</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          name="total_monthly_emi"
                          value={newLead.total_monthly_emi}
                          onChange={handleNewLeadChange}
                          placeholder="Enter total monthly EMI"
                          min="0"
                          step="any"
                        />
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid monthly EMI value.
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
             <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Select Product</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Product"
                  isClearable
                  isSearchable
                  isLoading={loadingProducts}
                  options={products.map(product => ({
                    value: product.id.toString(),
                    label: `${product.name} - ${product.bank?.bank || 'N/A'}`
                  }))}
                  value={currentProductOption}
                  onChange={(option) => handleSelectChange('product_id', option)}
                  formatOptionLabel={(option) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FaChartLine className="me-2" />
                      <span>{option.label}</span>
                    </div>
                  )}
                />
                 <Form.Control
                  hidden
                  value={newLead.product_id} // Keep hidden control for potential validation if needed later
                />
                <Form.Text className="text-muted">
                  Product selection is optional. If no product is selected, no case will be created.
                </Form.Text>
                {loadingProducts && (
                  <div className="text-center mt-2">
                    <Spinner size="sm" animation="border" /> Loading products...
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Conditionally render the Agent selection row only if no initialAgentId is provided */}
          {!initialAgentId && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assign to Agent</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select Agent"
                  isClearable
                  isLoading={loadingAgents}
                  options={agents.map(agent => ({
                    value: agent.id.toString(),
                    label: agent.name
                  }))}
                  value={currentAgentOption}
                  onChange={(option) => handleSelectChange('agent_id', option)}
                  isDisabled={!!initialAgentId} // Disable if agent is pre-selected
                />
                 <Form.Control
                  hidden
                  required // Keep hidden control for validation
                  value={newLead.agent_id}
                  isInvalid={validated && !newLead.agent_id}
                />
                <Form.Control.Feedback type="invalid">
                    Please select an agent.
                </Form.Control.Feedback>
                {!initialAgentId && (
                    <Form.Text className="text-muted">
                        Please select an agent to assign this lead to.
                    </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={addLeadLoading}
          >
            {addLeadLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Adding...
              </>
            ) : (
              <>
                <FaPlus className="me-1" /> Add Lead
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddLeadForm;
